/**
 * Web Share Target plumbing (docs/ux/ios-share-extension.md, PLAN G15). The service worker
 * receives `POST /share/target` (multipart), parks the payload in a private Cache Storage bucket
 * and redirects to `/share?stashed=1`; the Share screen takes it out exactly once. Nothing is
 * sent anywhere from the worker: the payload is sealed on this device when the user taps Send.
 *
 * Also the Shortcuts-friendly handoff: `/share?project=<id|name>&text=…&url=…&title=…` (text
 * only; files cannot ride on a URL).
 */

export const SHARE_ACTION = '/share/target'
export const SHARE_STASH = 'viko-share-stash'
const META = '/share/stash/meta'
/** Stale stashes (a share the user never finished) are ignored after a day. */
export const STASH_TTL_MS = 24 * 60 * 60_000
/** Big enough for screenshots and PDFs; the encrypted upload path enforces its own limit. */
export const SHARE_ACCEPT = [
  'image/*',
  'application/pdf',
  'text/*',
  '.md',
  '.txt',
  '.csv',
  '.json',
  '.doc',
  '.docx',
  '.rtf',
]

export interface SharedPayload {
  text: string
  files: File[]
  /** `project` id or name from a Shortcuts handoff. */
  project?: string
}

interface StashMeta {
  text: string
  at: number
  files: { key: string; name: string; type: string }[]
}

/** The text a share carries: title, body and URL, in that order, blank parts dropped. */
export function sharedText(form: { get(name: string): unknown }): string {
  return ['title', 'text', 'url']
    .map((k) => form.get(k))
    .filter((v): v is string => typeof v === 'string' && v.trim() !== '')
    .map((v) => v.trim())
    .filter((v, i, all) => all.indexOf(v) === i)
    .join('\n')
}

/** Service worker side: store the POSTed form. Files come as any field name (`media`, `files`). */
export async function stashShare(
  form: FormData,
  cache: Pick<Cache, 'put'>,
  now = Date.now(),
): Promise<number> {
  const files: StashMeta['files'] = []
  let i = 0
  for (const [, v] of form.entries()) {
    if (typeof v === 'string') continue
    const f = v as File
    const key = `/share/stash/file-${now}-${i++}`
    await cache.put(key, new Response(f, { headers: { 'Content-Type': f.type } }))
    files.push({ key, name: f.name, type: f.type })
  }
  const meta: StashMeta = { text: sharedText(form), at: now, files }
  await cache.put(
    META,
    new Response(JSON.stringify(meta), { headers: { 'Content-Type': 'application/json' } }),
  )
  return files.length
}

/** Page side: take the stash out (exactly once). */
export async function takeStash(
  cache: Pick<Cache, 'match' | 'delete'>,
  now = Date.now(),
): Promise<SharedPayload> {
  const res = await cache.match(META)
  if (!res) return { text: '', files: [] }
  let meta: StashMeta
  try {
    meta = (await res.json()) as StashMeta
  } catch {
    await cache.delete(META)
    return { text: '', files: [] }
  }
  const files: File[] = []
  const fresh = now - meta.at <= STASH_TTL_MS
  for (const f of meta.files) {
    const r = await cache.match(f.key)
    if (r && fresh)
      files.push(new File([await r.blob()], f.name || 'shared file', { type: f.type }))
    await cache.delete(f.key)
  }
  await cache.delete(META)
  return fresh ? { text: meta.text, files } : { text: '', files: [] }
}

export async function takeShared(): Promise<SharedPayload> {
  if (!('caches' in globalThis)) return { text: '', files: [] }
  return takeStash(await caches.open(SHARE_STASH))
}

/** `/share?text=&url=&title=&project=`: the URL handoff used by iOS Shortcuts. */
export function parseShareParams(search: string): {
  text: string
  project?: string
  stashed: boolean
} {
  const p = new URLSearchParams(search)
  const cap = (s: string | null) => (s ?? '').slice(0, 20_000)
  const text = sharedText({ get: (k) => cap(p.get(k)) })
  const project = cap(p.get('project')).trim().slice(0, 200)
  return { text, ...(project ? { project } : {}), stashed: p.get('stashed') === '1' }
}
